import { useState, useEffect } from "react";
import colorsQueries from "../../../../../../requests/queries/ColorsQueries";
import "./ColorPicker.css";
import { useParams } from "react-router-dom";

// Utilitaires de conversion de couleurs
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
};

const rgbaToHexAndOpacity = (rgba) => {
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (match) {
        const r = parseInt(match[1]).toString(16).padStart(2, '0');
        const g = parseInt(match[2]).toString(16).padStart(2, '0');
        const b = parseInt(match[3]).toString(16).padStart(2, '0');
        const a = match[4] ? parseFloat(match[4]) : 1;
        return { hex: `#${r}${g}${b}`, opacity: Math.round(a * 100) };
    }
    return null;
};

const parseColorValue = (value) => {
    if (!value) return { hex: "#000000", opacity: 100 };
    
    // Si c'est du rgba
    if (value.startsWith('rgba') || value.startsWith('rgb')) {
        const parsed = rgbaToHexAndOpacity(value);
        if (parsed) return parsed;
    }
    
    // Si c'est du hex avec 8 caractères (inclut l'alpha)
    if (value.match(/^#[a-f\d]{8}$/i)) {
        const hex = value.slice(0, 7);
        const alpha = parseInt(value.slice(7, 9), 16) / 255;
        return { hex, opacity: Math.round(alpha * 100) };
    }
    
    // Sinon c'est du hex classique
    return { hex: value, opacity: 100 };
};

const formatColorOutput = (hex, opacity) => {
    if (opacity >= 100) return hex;
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${(opacity / 100).toFixed(2)})`;
};

export default function ColorPicker({ value, onChange }) {

    const { id } = useParams();

    const [colors, setColors] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [customColor, setCustomColor] = useState("#000000");
    const [opacity, setOpacity] = useState(100);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            getColors();
        }
    }, [id, isOpen]);

    useEffect(() => {
        const parsed = parseColorValue(value);
        setCustomColor(parsed.hex);
        setOpacity(parsed.opacity);
    }, [value]);

    const getColors = async () => {
        setIsLoading(true);
        try {
            const data = await colorsQueries.getColors(id);
            setColors(data || []);
        } catch (error) {
            console.error("Erreur lors du chargement des couleurs:", error);
            setColors([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleColorSelect = (color) => {
        const parsed = parseColorValue(color);
        setCustomColor(parsed.hex);
        setOpacity(parsed.opacity);
    };

    const handleCustomColorChange = (e) => {
        const newColor = e.target.value;
        setCustomColor(newColor);
    };

    const handleOpacityChange = (e) => {
        setOpacity(parseInt(e.target.value));
    };

    const handleApplyColor = () => {
        const finalColor = formatColorOutput(customColor, opacity);
        onChange(finalColor);
        setIsOpen(false);
    };
    
    // Couleur finale avec opacité pour la prévisualisation
    const previewColor = formatColorOutput(customColor, opacity);

    const handleAddColor = async () => {
        if (customColor && id) {
            try {
                await colorsQueries.createColor(id, customColor);
                getColors();
            } catch (error) {
                console.error("Erreur lors de l'ajout de la couleur:", error);
            }
        }
    };

    return (
        <div className="color-picker-container">
            {/* Bouton principal avec prévisualisation */}
            <button 
                type="button"
                className="color-picker-trigger"
                onClick={() => setIsOpen(true)}
            >
                <span 
                    className="color-picker-preview" 
                    style={{ backgroundColor: value || "#000000" }}
                />
                <span className="color-picker-value">{value || "#000000"}</span>
                {opacity < 100 && <span className="color-picker-opacity-badge">{opacity}%</span>}
            </button>

            {/* Modal */}
            {isOpen && (
                <>
                    <div 
                        className="color-picker-overlay" 
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="color-picker-modal">
                        {/* Header */}
                        <div className="color-picker-header">
                            <h3 className="color-picker-title">🎨 Sélecteur de couleur</h3>
                            <button 
                                type="button"
                                className="color-picker-close"
                                onClick={() => setIsOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Contenu */}
                        <div className="color-picker-content">
                            {/* Prévisualisation grande */}
                            <div className="color-picker-preview-large">
                                <div 
                                    className="color-picker-preview-box"
                                    style={{ backgroundColor: previewColor }}
                                >
                                    {/* Damier pour montrer la transparence */}
                                    <div className="color-picker-checkerboard" />
                                    <div 
                                        className="color-picker-preview-overlay"
                                        style={{ backgroundColor: previewColor }}
                                    />
                                </div>
                                <div className="color-picker-preview-info">
                                    <span className="color-picker-preview-hex">{previewColor}</span>
                                    <span className="color-picker-preview-label">Couleur sélectionnée</span>
                                </div>
                            </div>

                            {/* Section couleur personnalisée */}
                            <div className="color-picker-section">
                                <label className="color-picker-label">Couleur personnalisée</label>
                                <div className="color-picker-custom">
                                    <input
                                        type="color"
                                        value={customColor}
                                        onChange={handleCustomColorChange}
                                        className="color-picker-input-native"
                                    />
                                    <input
                                        type="text"
                                        value={customColor}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setCustomColor(val);
                                        }}
                                        placeholder="#000000"
                                        className="color-picker-input-text"
                                    />
                                    {id && (
                                        <button 
                                            type="button"
                                            className="color-picker-save-btn"
                                            onClick={handleAddColor}
                                            title="Sauvegarder dans la palette"
                                        >
                                            💾
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Section opacité */}
                            <div className="color-picker-section">
                                <label className="color-picker-label">
                                    Opacité
                                    <span className="color-picker-opacity-value">{opacity}%</span>
                                </label>
                                <div className="color-picker-opacity">
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={opacity}
                                        onChange={handleOpacityChange}
                                        className="color-picker-opacity-slider"
                                        style={{
                                            background: `linear-gradient(to right, transparent 0%, ${customColor} 100%)`
                                        }}
                                    />
                                    <div className="color-picker-opacity-input-wrapper">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={opacity}
                                            onChange={handleOpacityChange}
                                            className="color-picker-opacity-input"
                                        />
                                        <span className="color-picker-opacity-unit">%</span>
                                    </div>
                                </div>
                            </div>

                            {/* Couleurs du projet */}
                            {colors.length > 0 && (
                                <div className="color-picker-section">
                                    <label className="color-picker-label">Couleurs du projet</label>
                                    <div className="color-picker-grid project-grid">
                                        {colors.map((color) => (
                                            <button
                                                key={color.id}
                                                type="button"
                                                className={`color-picker-swatch project-color ${customColor === color.color ? 'selected' : ''}`}
                                                style={{ backgroundColor: color.color }}
                                                onClick={() => setCustomColor(color.color)}
                                                title={color.color}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Palette de couleurs prédéfinies */}
                            <div className="color-picker-section">
                                <label className="color-picker-label">Couleurs de base</label>
                                <div className="color-picker-grid">
                                    {[
                                        "#000000", "#434343", "#666666", "#999999", "#b7b7b7", "#cccccc", "#d9d9d9", "#efefef", "#f3f3f3", "#ffffff",
                                        "#980000", "#ff0000", "#ff9900", "#ffff00", "#00ff00", "#00ffff", "#4a86e8", "#0000ff", "#9900ff", "#ff00ff",
                                        "#e6b8af", "#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3", "#d0e0e3", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc",
                                        "#dd7e6b", "#ea9999", "#f9cb9c", "#ffe599", "#b6d7a8", "#a2c4c9", "#a4c2f4", "#9fc5e8", "#b4a7d6", "#d5a6bd",
                                        "#cc4125", "#e06666", "#f6b26b", "#ffd966", "#93c47d", "#76a5af", "#6d9eeb", "#6fa8dc", "#8e7cc3", "#c27ba0",
                                    ].map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            className={`color-picker-swatch ${customColor === color ? 'selected' : ''}`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => setCustomColor(color)}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            </div>

                            {isLoading && (
                                <div className="color-picker-loading">
                                    <span className="color-picker-spinner"></span>
                                    <span>Chargement des couleurs...</span>
                                </div>
                            )}
                        </div>

                        {/* Footer avec boutons */}
                        <div className="color-picker-footer">
                            <button 
                                type="button"
                                className="color-picker-btn color-picker-btn-cancel"
                                onClick={() => setIsOpen(false)}
                            >
                                Annuler
                            </button>
                            <button 
                                type="button"
                                className="color-picker-btn color-picker-btn-apply"
                                onClick={handleApplyColor}
                            >
                                Appliquer
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
